import { MessageBarType, Toggle } from "office-ui-fabric-react";
import React from "react";
import { IRootState } from '../../../store';
import { connect } from "react-redux";
import { Component } from "react";
import { Assignment } from "../../../models/analytics/Assignment";
import Table, { TableColumn } from "../../Table";
import { TableRowProps } from "../../TableRow";
import UserSelector from "../../UserSelector";
import { UserOption } from "../../UserSelectorBase";
import ApiService from "../../ApiService";
import StudentAssignmentStatusPill from "./StudentAssignmentStatusPill";
import DispatchProps from "../../../utils/DispatchProps";
import Actions from "../../../redux/Actions";
import TableCell from "../../TableCell";
import * as H from "history";
import { StudentAssignmentSearchQuery } from "../../../models/StudentAssignmentSearchQuery";
import SchoolMenu from "../../SchoolMenu";
import SchoolPageTitle from "../../SchoolPageTitle";

interface StudentAssignmentSearchPageProps {
    tenantId: string,
    schoolCode: string,
    apiService: ApiService,
    history: H.History
}

interface StudentAssignmentSearchPageState {
    query?: StudentAssignmentSearchQuery;
    selectedUserId?: string;
    selectedUserName?: string;
    loading: boolean;
    onlyShowCurrentAssignments?: boolean;
    assignmentToPassToDrillDownPage?: Assignment;
    assignmentsToShow: Assignment[]
    assignments: Assignment[]
}

class StudentAssignmentSearchPage extends Component<StudentAssignmentSearchPageProps & DispatchProps, StudentAssignmentSearchPageState>
{
    constructor(props: StudentAssignmentSearchPageProps & DispatchProps) {
        super(props);

        this.state = {
            loading: false,
            assignmentsToShow: [],
            assignments: []
        };
    }
    
    async componentDidMount(): Promise<void> {
        this.loadFromQuery();
    }

    async componentDidUpdate(prevProps: Readonly<StudentAssignmentSearchPageProps & DispatchProps>, prevState: StudentAssignmentSearchPageState) {
        //State has been changed internally
        if (this.state.selectedUserId !== prevState.selectedUserId || this.state.onlyShowCurrentAssignments !== prevState.onlyShowCurrentAssignments) {
            this.updateQuery();
        }

        //Check if user in query has changed outside of update query method
        var query = StudentAssignmentSearchQuery.fromQueryString(this.props.history.location.search);
        if (query.userId !== this.state.query?.userId && query.userId !== this.state.selectedUserId) {
            this.loadFromQuery();
        }
    }     

    private loadFromQuery(): void {
        var query = StudentAssignmentSearchQuery.fromQueryString(this.props.history.location.search);

        this.setState({
            query: query,
            selectedUserId: query.userId,
            onlyShowCurrentAssignments: query.onlyCurrent ?? true,
            selectedUserName: undefined
        })

        if (query?.userId) {
            this.retrieveAssignmentDataByUserId(query.userId!);
        }
    }

    private updateQuery(): void {
        //Load the current query
        var query = StudentAssignmentSearchQuery.fromQueryString(this.props.history.location.search);

        //if values in query dont match new values then needs updating
        if (query.userId !== this.state.selectedUserId || query.onlyCurrent !== this.state.onlyShowCurrentAssignments) {

            const queryString = ApiService.buildQueryString({
                "userId": this.state.selectedUserId,
                "onlyCurrent": this.state.onlyShowCurrentAssignments
            });

            //When user has changed add to stack to allow history of searches
            if (query.userId && query.userId !== this.state.selectedUserId) {
                this.props.history.push({ search: queryString })
            }
            else {
                this.props.history.replace({ search: queryString });
            }

            this.setState({
                query: {
                    userId: this.state.selectedUserId,
                    onlyCurrent: this.state.onlyShowCurrentAssignments
                }
            })
        }
    }
    
    private cellForColumn = (item: Assignment, column: TableColumn, columnIndex: number): JSX.Element | undefined => {
        if (column.propertyPath === "status") {
            return <TableCell flex={column.flex} key={columnIndex}>
                <StudentAssignmentStatusPill key={item.assignmentId} assignment={item} submission={item.submissions[0]}></StudentAssignmentStatusPill>
            </TableCell>
        }
    }

    private async retrieveAssignmentData(user: UserOption) {        
        this.setState({
            selectedUserId: user?.id,
            selectedUserName: user?.displayName
        })

        this.retrieveAssignmentDataByUserId(user?.id);
    }
    
    private async retrieveAssignmentDataByUserId(userId: string) {       

        this.setState({
            assignmentsToShow: []
        });

        if (!this.props.tenantId || !this.props.schoolCode || !userId) {
            return;
        }

        this.setState({
            loading: true
        });

        try {
            var assignments = await this.props.apiService.searchStudentAssignments(this.props.tenantId, this.props.schoolCode, userId);
                     
            this.setState({
                assignments: assignments,
            });

            this.filterAssignments(this.state.onlyShowCurrentAssignments);
        }
        catch (e) {

            if (e instanceof DOMException && (e.name == "AbortError" || e.message === "The user aborted a request." || e.message === "signal is aborted without reason")) {
                console.log(e)
            }
            else {
                console.log(e);
                this.props.dispatch(Actions.showMessageBar(`Unable to fetch assignments: ${e.message}`, MessageBarType.error));
            }
            
        }

        this.setState({ loading: false });
    }

    private filterAssignments(onlyShowCurrentAssignments: boolean | undefined) {
        this.setState({
            assignmentsToShow: onlyShowCurrentAssignments ? this.state.assignments?.filter(ass => ass.isActive)! : this.state.assignments
        });
    }

    private columnRowProps = (item: Assignment): TableRowProps => {
        return {
            enableRowLink: true,
            to: `/manage/${this.props.tenantId}/${this.props.schoolCode}/student-assignment/${item.classId}/${item.assignmentId}/${this.state.selectedUserId}`,
        };
    };

    private onToggleChanged = (event: React.MouseEvent<HTMLElement, MouseEvent>, newValue: boolean | undefined) => {
        this.setState({ onlyShowCurrentAssignments: newValue });       

        this.filterAssignments(newValue);
    };

    private static readonly columns: TableColumn[] =
        [
            { title: "Name", propertyPath: "displayName", enableSort: true, flex: 2 },
            { title: "Class", propertyPath: "classDisplayName", enableSort: true, flex: 1 },
            { title: "Subject", propertyPath: "subject.subjectName", enableSort: true, flex: 1 },
            { title: "Created Date", propertyPath: "createdDateTime", enableSort: true, flex: 1, type: "date" },
            { title: "Due Date", propertyPath: "dueDateTime", enableSort: true, flex: 1, type: "date" },
            { title: "Status", propertyPath: "status", enableSort: true, flex: 1 }
        ];

    public render(): JSX.Element {
        return (
            <div>
                <SchoolPageTitle tenantId={this.props.tenantId} schoolCode={this.props.schoolCode} title="Student Assignment Search" reverse={true} />
                <SchoolMenu prefix="Student Assignment Search" />

                <div className={"cdb-admin-content cdb-admin-content--column"}>
                    
                    <div className="p-1rem flex-row flex--justify-content-flex-start">
                        <UserSelector
                            tenantId={this.props.tenantId}
                            className=""
                            label={"Pick a student"}
                            onSelectionChanged={(selectedUser: UserOption[]) => {
                                this.retrieveAssignmentData(selectedUser[0]);
                            }}
                            disabled={this.state.loading}
                            multiSelect={false}
                            selectedUsers={this.state.selectedUserId ? [this.state.selectedUserId] : []}
                            additionalFilter={["Student"]}
                        />
                        <Toggle
                            className={"p-left-1rem p-top-1rem"}
                            inlineLabel={true}
                            disabled={this.state.loading}
                            checked={this.state.onlyShowCurrentAssignments}
                            onChange={(event, newValue) => {
                                this.onToggleChanged(event, newValue);
                            }}
                            role="switch"
                            label={"Only show current assignments"}
                        />
                    </div>
    
                    <Table
                        columns={StudentAssignmentSearchPage.columns}
                        items={this.state.assignmentsToShow ? this.state.assignmentsToShow : []}
                        loading={this.state.loading}
                        title={this.state.selectedUserName ? `${this.state.selectedUserName} Assignments` : "Assignments"}
                        cellForColumn={this.cellForColumn}
                        className={"cdb-table--enable-hover"}
                        rowPropsForItem={this.columnRowProps}
                    />
                </div>
            </div>
        );
    }
}

export default connect(
    (state: IRootState, ownProps: { history: H.History, match: any }): StudentAssignmentSearchPageProps => {
        return {
            tenantId: ownProps.match.params.tenantId,
            schoolCode: ownProps.match.params.schoolCode,
            apiService: state.auth.apiService,
            history: ownProps.history,
        };
    }
)(StudentAssignmentSearchPage)